import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import DataSet from '@antv/data-set';
import { STOPLIGHT_COLORS } from '../../utils/DataFormatterUtils';
import { Col, Row } from 'antd';

const BudgetChart: FC<any> = ({
	data,
	budgetTotal,
	currency,
	remaining,
	consumed,
	recordType,
	itemAmount,
	consumedAmount,
	remainingAmount,
	threshold,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState(400);

	const handleResize = useCallback(() => {
		const current = containerRef.current;
		const parent = current ? current.parentElement : null;
		if (parent) {
			setContainerWidth(parent.offsetWidth);
		}
	}, []);

	useEffect(() => {
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [handleResize]);

	const budgetBorderColor =
		budgetTotal < itemAmount + consumedAmount
			? STOPLIGHT_COLORS.pureRed
			: consumedAmount + itemAmount > threshold
			? '#D79128'
			: STOPLIGHT_COLORS.pureGreen;

	if (!Array.isArray(data) || data.length === 0) return null;

	const safeBudgetTotal = Number(budgetTotal ? budgetTotal : 0);

	const fields = [
		...(consumed ? ['Budget Used'] : []),
		`This ${recordType}`,
		...(remaining ? ['Remaining Budget'] : []),
	];

	const ds = new DataSet();
	const dv = ds.createView().source(data);

	dv.transform({
		type: 'fold',
		fields,
		key: 'type',
		value: 'spend',
		retains: ['budget'],
	});

	dv.transform({
		type: 'map',
		callback: (row) => ({ ...row, spend: Number(row.spend ? row.spend : 0) }),
	});
	const formatLabel = (spend) =>
		safeBudgetTotal === 0 ? `` : `${((Number(spend || 0) / safeBudgetTotal) * 100).toFixed(0)}% • `;

	const itemColor = '#D3ADF7';
	const accumulatedColor = '#53298e';

	return (
		<Col
			style={{
				display: 'flex',
				flexGrow: 1,
				flexDirection: 'column',
				width: '100%',
				minWidth: 0,
				marginTop: 20,
				marginBottom: 10,
				paddingLeft: 0,
				paddingRight: 0,
			}}
			ref={containerRef}
		>
			<Row style={{ width: '100%', position: 'relative', height: 30, marginBottom: 8 }}>
				<div
					style={{
						display: 'flex',
						width: '100%',
						height: '100%',
						overflow: 'visible',
						position: 'relative',
						zIndex: 10,
					}}
				>
					{consumed > 0 && (
						<div
							style={{
								width: `${
									(parseFloat(consumedAmount) /
										(parseFloat(consumedAmount) +
											parseFloat(itemAmount) +
											(remaining ? parseFloat(remainingAmount) : 0))) *
									100
								}%`,
								background: accumulatedColor,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								color: '#fff',
								fontSize: 12,
								overflow: 'hidden',
							}}
						/>
					)}
					<div
						style={{
							width: `${
								(parseFloat(itemAmount) /
									(parseFloat(consumedAmount) +
										parseFloat(itemAmount) +
										(remaining ? parseFloat(remainingAmount) : 0))) *
								100
							}%`,
							background: itemColor,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: '#fff',
							fontSize: 12,
							overflow: 'hidden',
						}}
					/>
					{remaining && (
						<div
							style={{
								flex: 1,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								color: '#fff',
								fontSize: 12,
								overflow: 'hidden',
							}}
						/>
					)}

					{Number.isFinite(safeBudgetTotal) &&
						safeBudgetTotal > 0 &&
						(() => {
							const total =
								parseFloat(consumedAmount) +
								parseFloat(itemAmount) +
								(remaining ? parseFloat(remainingAmount) : 0);
							const pct = Math.min((safeBudgetTotal / total) * 100, 100);
							return (
								<>
									<div
										style={{
											position: 'absolute',
											left: 0,
											top: -10,
											width: `${pct}%`,
											height: 50,
											backgroundColor: '#F0F0F0',
											zIndex: -1,
										}}
									/>
									<div
										style={{
											position: 'absolute',
											left: 0,
											top: -10,
											width: `${pct}%`,
											height: 50,
											border: `2px solid ${budgetBorderColor}`,
											pointerEvents: 'none',
											boxSizing: 'border-box',
											zIndex: 11,
										}}
									/>
								</>
							);
						})()}
				</div>
			</Row>
			<Row
				style={{
					display: 'flex',
					gap: 16,
					padding: '8px 0',
					fontSize: 13,
					color: '#444',
					flexWrap: 'wrap',
				}}
			>
				{consumed > 0 && (
					<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<span
							style={{
								width: 12,
								height: 12,
								background: accumulatedColor,
								display: 'inline-block',
							}}
						/>
						Budget Used:&nbsp;
						<span style={{ color: 'black' }}>
							<span style={{ color: '#bbb' }}>{formatLabel(consumedAmount)}</span>
							<span
								style={{
									color: consumedAmount > budgetTotal ? STOPLIGHT_COLORS.pureRed : 'black',
								}}
							>
								{currency.format(consumedAmount)}
							</span>{' '}
							/ {currency.format(safeBudgetTotal)}
						</span>
					</span>
				)}
				<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
					<span style={{ width: 12, height: 12, background: itemColor, display: 'inline-block' }} />
					This {recordType}:&nbsp;
					<span style={{ color: '#bbb' }}>
						{formatLabel(itemAmount)}{' '}
						<span style={{ color: 'black' }}>{currency.format(itemAmount)}</span>
					</span>
				</span>
			</Row>
		</Col>
	);
};

export default BudgetChart;
